<template>
  <div class="select-action-modal">
    <div class="action-options">
      <div
        v-for="action in actions"
        :key="action.value"
        class="action-option"
        :class="{ 'action-option--disabled': action.disabled }"
        :title="action.disabledReason || ''"
        @click="!action.disabled && selectAction(action.value)"
      >
        <div class="action-option__icon">
          <BaseIcon :name="action.icon" :size="action.iconSize || 24" />
        </div>
        <div class="action-option__body">
          <h3>{{ action.label }}</h3>
          <p
            v-if="action.disabled && action.disabledReason"
            class="action-option__disabled-reason"
          >
            {{ action.disabledReason }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { computed } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { DEAL_TRIGGER_TYPES, TASK_CONTEXT_TRIGGER_TYPES } from '@/constants/automations';

  const ALL_ACTIONS = [
    { label: 'Integration', icon: 'link', value: 'integration' },
    { label: 'Task', icon: 'bullseye', value: 'task' },
    { label: 'Condition', icon: 'split', value: 'condition' },
    {
      label: 'Verplaats deal naar stage',
      icon: 'arrow-right',
      value: 'move_deal_stage',
      requiresDealTrigger: true,
    },
    {
      label: 'Toewijzen aan persoon',
      icon: 'user',
      value: 'assign_user',
      requiresDealTrigger: true,
    },
    {
      label: 'Maak taak aan',
      icon: 'clipboard',
      value: 'create_deal_task',
      requiresTaskContext: true,
    },
    { label: 'Delay', icon: 'calendar-clock', value: 'delay' },
  ];

  export default {
    name: 'SelectActionModal',
    components: {
      BaseIcon,
    },
    props: {
      triggerType: {
        type: String,
        default: null,
      },
    },
    setup(props, { emit }) {
      const isDealTrigger = computed(() =>
        DEAL_TRIGGER_TYPES.includes(props.triggerType)
      );

      const isTaskContextTrigger = computed(() =>
        TASK_CONTEXT_TRIGGER_TYPES.includes(props.triggerType)
      );

      const actions = computed(() =>
        ALL_ACTIONS.map((a) => {
          if (a.requiresDealTrigger && !isDealTrigger.value) {
            return {
              ...a,
              disabled: true,
              disabledReason: 'Alleen beschikbaar bij deal-triggers',
            };
          }
          if (a.requiresTaskContext && !isTaskContextTrigger.value) {
            return {
              ...a,
              disabled: true,
              disabledReason: 'Alleen bij deal-, project- of werkbon-triggers',
            };
          }
          return a;
        })
      );

      const selectAction = (action) => {
        emit('submit', action);
      };

      return {
        actions,
        selectAction,
      };
    },
  };
</script>

<style scoped lang="scss">
  .select-action-modal {
    .action-options {
      display: grid;
      gap: 16px;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    }

    .action-option {
      padding: 20px;
      border: 1px solid var(--slate-200);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 16px;

      &:hover {
        border-color: var(--brand-500);
        box-shadow: 0 4px 4px rgba(0, 0, 0, 0.05);
      }

      &--disabled {
        cursor: not-allowed;
        opacity: 0.5;

        &:hover {
          border-color: var(--slate-200);
          box-shadow: none;
        }
      }

      &__icon {
        width: 48px;
        height: 48px;
        border-radius: 8px;
        border: 1px solid var(--slate-200);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        background-color: var(--slate-50);
      }

      &__body {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;

        h3 {
          margin: 0;
        }
      }

      &__disabled-reason {
        margin: 0;
        font-size: 0.75rem;
        color: var(--slate-500);
      }
    }
  }
</style>
